import { memo, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { format, isBefore, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';

import Button from '../../widgets/Button';
import DatePresetsPicker from '../Filters/DateFilter/DatePresetsPicker';
import BottomSheet from '../Layout/Header/BottomSheet/BottomSheet';
import { HeadingFilterSkeleton } from '../../widgets/Skeletons';

import { useDeviceType } from '../../../utilities/common/deviceType';
import useFilters from '../../../utilities/hooks/useFilters';
import { useWidgetsModalContext } from '../../../shared/providers/WidgetsModalProvider';
import { useFilterData } from '../../../utilities/hooks/useFilterData';

import FilterIcon from '../../../assets/icons/filter.svg?react';
import infoIcon from '../../../assets/icons/info.svg';
import { SelectedBenchmark } from '../../../shared/constants/filter/filter';
import BenchmarkFilter from './BenchmarkFilter';
import { DateNotice } from './DateNotice';
import IndustryStandardFilter from '../IndustryStandards/components/IndustryStandardFilter';
import { ChevronBottomIcon } from '../../../assets/icons/ChevronBottomIcon';
import { SlidersHorizontalIcon } from '../../../assets/icons/SlidersHorizontal';
import { useIsFilterAllowed } from './hooks';

interface IHeadingFilterProps {
  loading: boolean;
  meta?: any;
  layoutSubtitle?: string | null;
}

function HeadingFilter({ loading, meta, layoutSubtitle }: IHeadingFilterProps) {
  const isFilterAllowed = useIsFilterAllowed();
  const [isScrolled, setIsScrolled] = useState(false);
  const [sheetDatePicker, setSheetDatePicker] = useState(false);

  const {
    setShowFilterModal,
    setMobileShowFilterModal,
    widgetLoadingStates,
  } = useWidgetsModalContext();
  const {
    getFilters,
    dateRange: { fromDate, toDate },
  } = useFilters();

  const [selectedRadio, setSelectedRadio] = useState<SelectedBenchmark>(
    SelectedBenchmark.GLOBAL,
  );

  const { configData } = useFilterData();

  const isMobile = useDeviceType();
  const { t } = useTranslation();
  const { slug } = useParams();

  const handleScroll = () => setIsScrolled(window.scrollY > 72);

  const isLegacyDimensionPage =
    slug === 'dimensions-legacy' || !layoutSubtitle;

  const isOrgDim =
    slug === 'dimensions' ||
    (slug?.startsWith('dimensions-') && !isLegacyDimensionPage);

  const isIndustryStandardPage = slug === 'industry-standards';

  const numberOfAppliedFilters = useMemo(() => {
    const { dateRange, selectedDepartments, selectedOrganisations, ...rest } =
      getFilters();

    const totalSelected = Object.values(rest).reduce(
      (accumulator, currentValue) =>
        accumulator + (Array.isArray(currentValue) ? currentValue.length : 0),
      0,
    );

    return (
      totalSelected +
      Array.from(selectedDepartments.values()).filter(Boolean).length +
      Array.from(selectedOrganisations.values()).filter(Boolean).length
    );
  }, [getFilters()]);

  useEffect(() => {
    function watchScroll() {
      window.addEventListener('scroll', handleScroll);
    }

    watchScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  function handleSheet(open: boolean) {
    const element = document.querySelector('.hrdb-body-container');
    if (element) {
      if (open) {
        element.classList.add('z-[99]');
        setSheetDatePicker(true);
      } else {
        element.classList.remove('z-[99]');
        setSheetDatePicker(false);
      }
    }
  }

  const [isDateBeforeContractDate, setIsDateBeforeContractDate] =
    useState(false);

  useEffect(() => {
    const secondDateStr = format(parseISO(fromDate), 'yyyy, M, dd');

    if (configData && configData.contractStartDate) {
      const contractDate = parseISO(configData.contractStartDate);
      const formattedContractDate = format(contractDate, 'yyyy, M, dd');

      const newDate = isBefore(
        new Date(secondDateStr),
        new Date(formattedContractDate),
      );
      setIsDateBeforeContractDate(newDate);
    }
  }, [configData, fromDate]);

  return (
    <>
      <div
        className={`bg-taupe-200 z-[11] transform pb-6 transition-[padding,shadow] duration-300 md:px-6 ${
          !isMobile && 'sticky top-[72px] -mr-6 -ml-6'
        } ${isScrolled && !isMobile && 'pt-6 shadow-sm'}`}
      >
        {loading ? (
          <HeadingFilterSkeleton />
        ) : (
          <>
            <div
              className={`mb-2 items-center gap-x-[9px] md:justify-between md:gap-x-0 ${isFilterAllowed ? 'flex' : 'hidden'}`}
            >
              <div>
                {slug?.startsWith('dimensions-') && isLegacyDimensionPage && (
                  <div className="border-black-300 bg-black-200 rounded-lg border px-4 py-2 font-semibold">
                    {t('LATEST_6_MONTH_DATA')}
                  </div>
                )}
                <DatePresetsPicker
                  isMobileSheetVisible={false}
                  setMobileSheetOpen={handleSheet}
                  dataLastUpdatedDate={meta?.lastUpdatedAt || toDate}
                />
              </div>

              {isMobile && (
                <BottomSheet
                  show={sheetDatePicker}
                  onHide={() => handleSheet(false)}
                >
                  <div className="max-h-[85vh] overflow-auto pb-2">
                    <div className="text-black-900 px-6 py-5 text-lg font-semibold">
                      {t('SELECT_TIME')}
                    </div>
                    {isMobile && (
                      <div className="mt-2 px-6">
                        <DatePresetsPicker
                          isMobileSheetVisible={true}
                          setMobileSheetOpen={handleSheet}
                          dataLastUpdatedDate={meta?.lastUpdatedAt || toDate}
                        />
                      </div>
                    )}
                  </div>
                </BottomSheet>
              )}

              <Button
                variant="outline"
                className={`flex gap-x-2 select-none ${
                  Object.keys(widgetLoadingStates).length === 0 && ''
                }`}
                leftIcon={<SlidersHorizontalIcon />}
                rightIcon={<ChevronBottomIcon />}
                onClick={() => {
                  isMobile
                    ? setMobileShowFilterModal(true)
                    : setShowFilterModal(true);
                }}
              >
                {t('MORE_FILTER')}
                {numberOfAppliedFilters > 0 && (
                  <span
                    className={`bg-primary-600 flex h-full w-5 items-center justify-center rounded-full text-xs font-semibold text-white ${
                      numberOfAppliedFilters > 99 && 'w-7'
                    }`}
                  >
                    {numberOfAppliedFilters}
                  </span>
                )}
              </Button>
            </div>
            <div className="text-grey-700 flex flex-row gap-2 p-2 text-sm">
              <img src={infoIcon} alt="" />
              <DateNotice
                slug={slug}
                isLegacyDimensionPage={isLegacyDimensionPage}
                meta={meta}
                earlyDate={isDateBeforeContractDate}
                fromDate={fromDate}
                toDate={toDate}
                configData={configData}
              />
            </div>
          </>
        )}
      </div>

      {!loading && isOrgDim && (
        <BenchmarkFilter
          selectedRadio={selectedRadio}
          setSelectedRadio={setSelectedRadio}
        />
      )}

      {!loading && isIndustryStandardPage && <IndustryStandardFilter />}
    </>
  );
}

export default memo(HeadingFilter);
